import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BadgePercent, Check, ExternalLink, Link2, Package, Store } from 'lucide-react';
import { connect } from 'react-redux';
import { StorefrontService } from '../../../core/services/storefront.service';
import { priceParts } from './price';
import '../../../modules/shared/theme/theme.scss';
import './storefront-public.scss';

interface Props { UserReducer: any; }

const ProductDetailComponent: React.FC<Props> = ({ UserReducer }) => {
    const { slug, product_id } = useParams<{ slug: string; product_id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<any>(null);
    const [images, setImages] = useState<any[]>([]);
    const [selectedImg, setSelectedImg] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [linkCopied, setLinkCopied] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);

    const influencer_id = UserReducer?.influencer_id;
    const account_id = UserReducer?.account_id;

    useEffect(() => {
        if (!product_id) return;
        StorefrontService.getPublicProduct(Number(product_id))
            .then((res: any) => {
                if (res?.status === 1) {
                    setProduct(res.data.product);
                    setImages(res.data.images || []);
                } else {
                    setError('Product not found');
                }
            })
            .catch(() => setError('Failed to load product'))
            .finally(() => setLoading(false));
    }, [product_id]);

    const handleGetLink = async () => {
        if (!influencer_id) { navigate('/login'); return; }
        setLinkLoading(true);
        try {
            const res = await StorefrontService.generateAffiliateLink({
                product_id: Number(product_id), influencer_id, account_id
            });
            if (res?.data?.affiliate_url) {
                await navigator.clipboard.writeText(res.data.affiliate_url);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 3000);
            }
        } catch {}
        setLinkLoading(false);
    };

    const handleVisitSite = () => {
        if (!product) return;
        const separator = product.product_url.includes('?') ? '&' : '?';
        window.open(`${product.product_url}${separator}utm_source=clanconnect&utm_medium=storefront`, '_blank');
    };

    if (loading) return <div className="brd brd-pub"><div className="brd-center"><span className="brd-spinner" /></div></div>;
    if (error || !product) return (
        <div className="brd brd-pub">
            <div className="brd-empty">
                <span className="brd-empty__icon"><Package size={28} /></span>
                <p className="brd-empty__title">{error || 'Product not found'}</p>
                <button type="button" className="brd-btn brd-btn--secondary" onClick={() => navigate(`/store/${slug}`)}>Back to store</button>
            </div>
        </div>
    );

    const displayImages = images.length > 0 ? images : [];
    const price = priceParts(product);

    return (
        <div className="brd brd-pub">
            <main className="brd-container brd-pub__body">
                <button type="button" className="brd-btn brd-btn--ghost brd-btn--sm brd-pub-back" onClick={() => navigate(`/store/${slug}`)}>
                    <ArrowLeft size={16} /> Back to {product.store_name}
                </button>

                <div className="brd-pub-detail">
                    {/* Image gallery */}
                    <div>
                        <div className="brd-card brd-pub-detail__main">
                            {displayImages[selectedImg]?.image_url
                                ? <img src={displayImages[selectedImg].image_url} alt={product.product_title} />
                                : <Package size={48} />}
                        </div>
                        {displayImages.length > 1 && (
                            <div className="brd-pub-detail__thumbs">
                                {displayImages.map((img: any, idx: number) => (
                                    <button key={img.image_id} type="button" onClick={() => setSelectedImg(idx)}
                                        className={`brd-pub-detail__thumb${selectedImg === idx ? ' brd-pub-detail__thumb--on' : ''}`}
                                        aria-label={`Image ${idx + 1}`}>
                                        <img src={img.image_url} alt="" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product info */}
                    <div className="brd-pub-detail__info">
                        <button type="button" className="brd-pub-detail__brand" onClick={() => navigate(`/store/${slug}`)}>
                            {product.store_logo
                                ? <img src={product.store_logo} alt="" />
                                : <span><Store size={16} /></span>}
                            {product.store_name}
                        </button>

                        <h1 className="brd-pub-detail__title">{product.product_title}</h1>

                        <div className="brd-chip-row" style={{ gap: 6 }}>
                            {product.category && <span className="brd-pill brd-pill--primary">{product.category}</span>}
                            <span className={`brd-pill ${product.in_stock ? 'brd-pill--success' : 'brd-pill--muted'}`}>{product.in_stock ? 'In stock' : 'Out of stock'}</span>
                        </div>

                        {price && (
                            <p className="brd-pub-price brd-pub-price--lg">
                                <span className="brd-pub-price__now">{price.now}</span>
                                {price.was && <span className="brd-pub-price__was">{price.was}</span>}
                                {price.off && <span className="brd-pill brd-pill--success">{price.off}% off</span>}
                            </p>
                        )}

                        {product.commission_percent > 0 && (
                            <div className="brd-banner brd-pub-detail__earn">
                                <BadgePercent size={16} />
                                <span>Earn {product.commission_percent}% commission on each sale you drive</span>
                            </div>
                        )}

                        {/* CTA buttons */}
                        <div className="brd-pub-detail__ctas">
                            <button type="button" className={`brd-btn brd-btn--lg ${linkCopied ? 'brd-btn--success' : 'brd-btn--primary'}`}
                                onClick={handleGetLink} disabled={linkLoading} aria-busy={linkLoading}>
                                {linkLoading ? <span className="brd-spinner brd-spinner--sm brd-spinner--light" /> : linkCopied ? <Check size={18} /> : <Link2 size={18} />}
                                {linkCopied ? 'Affiliate Link Copied!' : 'Get My Affiliate Link'}
                            </button>
                            <button type="button" className="brd-btn brd-btn--outline brd-btn--lg" onClick={handleVisitSite}>
                                <ExternalLink size={18} /> Visit on Brand Site
                            </button>
                        </div>

                        {product.product_description && (
                            <section className="brd-pub-detail__desc">
                                <h2>Description</h2>
                                <p>{product.product_description}</p>
                            </section>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer });
export const ProductDetail = connect(mapStateToProps)(ProductDetailComponent);
